import { Component, OnInit,Input } from '@angular/core';
import { ApiService } from 'src/app/api/api.service';
import { GlobalService } from '../../../api/global.service';
import { NgxFileDropEntry, FileSystemFileEntry, FileSystemDirectoryEntry } from 'ngx-file-drop';
import { FolderSelectModalPage } from 'src/app/modals/files/folder-select-modal/folder-select-modal.page';
import { CreateFolderPage } from 'src/app/modals/files/create-folder/create-folder.page';
import { CommentPage } from 'src/app/modals/files/comment/comment.page';
import { FilePage } from 'src/app/pop-overs/files/file/file.page';
import { AlertController, ModalController, PopoverController } from '@ionic/angular';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'app-file-explorer',
  templateUrl: './explorer.component.html',
  styleUrls: ['./explorer.component.scss'],
})
export class ExplorerComponent implements OnInit {

  //This component only takes in the root path that it should use as home
  @Input() root_folder_guid: any = null;
  @Input() allow_hash_change = true;
  private _hide_empty_folders = false;
  get hide_empty_folders() { return this._hide_empty_folders; }
  set hide_empty_folders(v: boolean) { this._hide_empty_folders = v; this.UpdateViewCookies(); }

  private _list_view = false;
  get list_view() { return this._list_view; }
  set list_view(v: boolean) { this._list_view = v; this.UpdateViewCookies(); }

  private _sort_alpha: 'none' | 'asc' | 'desc' = 'none';
  get sort_alpha(): 'none' | 'asc' | 'desc' { return this._sort_alpha; }
  set sort_alpha(v: 'none' | 'asc' | 'desc') { this._sort_alpha = v; this.UpdateViewCookies(); }

  get SortedData(): any[] {
    if (!this.data) return [];
    // The synthetic "Important" tile always stays pinned first, regardless of sort mode.
    const important = this.data.filter(item => item.is_important_folder);
    const rest = this.data.filter(item => !item.is_important_folder);
    if (this.sort_alpha === 'none') return [...important, ...rest];
    const sorted_rest = [...rest].sort((a, b) => {
      const nameA = (a.is_file ? a.file_name : (a.folder_title || '')).toLowerCase();
      const nameB = (b.is_file ? b.file_name : (b.folder_title || '')).toLowerCase();
      return this.sort_alpha === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
    return [...important, ...sorted_rest];
  }

  data: any = null;
  current_folder: any = {
    guid: '0'
  };
  selected_files: any = [];
  viewing_important_folder = false;
  current_user = null;
  finished_loading = false;
  refreshing = false;
  // Folder listings are paged (100 at a time) so folders with thousands of items don't block the
  // initial render - subsequent pages load in the background via LoadRemainingPages(). Not used for
  // the Important flat view.
  private readonly PAGE_SIZE = 250;
  private load_generation = 0;
  loading_more_pages = false;

  nav_history = [];
  loaded_from_hash = false;
  generated_from_hash = false;
  loaded_breadcrumbs = false;


  //Dropzone files
  files: NgxFileDropEntry[] = [];

  // Read-only lookup of the firm's client roles (guid -> title), used to label folders/files that
  // are restricted to a role, matching the badge shown on the accountant side.
  role_titles_by_guid: { [guid: string]: string } = {};

  explorer_settings: any = {
    show_linked_to_tasks: true,
    show_linked_to_projects: true,
  }

  constructor(
    public apiService: ApiService,
    public globalService: GlobalService,
    private popoverController: PopoverController,
    private alertController: AlertController,
    private modalController: ModalController,
    private cookieService: CookieService) { }

  ngOnInit() {
    console.log(this.root_folder_guid);
    this.RestoreViewCookies();
    this.current_user = this.globalService.GetCurrentUser();
    // A browser refresh shouldn't try to restore into whatever folder the hash points at -
    // GenerateBreadCrumbs() walks back up parents to rebuild nav_history, but a client/company's own
    // root folder has no parent row to find, so that walk comes up empty and nav_history is left with
    // just this one folder. That made a real folder look like the explorer's outer root and incorrectly
    // disabled upload/create there. Deep links (e.g. a "Files" notification click, see top-navbar's
    // viewNotificationDetail) are a genuine navigation, not a reload, so they're unaffected - only an
    // actual F5/refresh drops the hash and falls back to the true root. Use the in-page refresh button
    // (RefreshCurrentFolder) instead of the browser's reload to get fresh data without losing your place.
    const nav_entries = performance.getEntriesByType?.('navigation') as PerformanceNavigationTiming[];
    const is_reload = nav_entries?.[0]?.type === 'reload';
    if (is_reload && window.location.hash) {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    if (window.location.hash.replace('#', '') && this.root_folder_guid == '0') {
      this.root_folder_guid = window.location.hash.replace('#', '');
      this.loaded_from_hash = true;
    }
    this.GetRoles();
    this.Get(this.root_folder_guid);
  }

  async GetRoles() {
    const return_data: any = await this.apiService.get('firms/clients/roles');
    if (!return_data?.success || !Array.isArray(return_data.data)) return;
    this.role_titles_by_guid = {};
    for (const role of return_data.data) {
      this.role_titles_by_guid[role.guid] = role.title;
    }
  }

  GetRoleTitle(role_guid: string): string | null {
    if (!role_guid) return null;
    return this.role_titles_by_guid[role_guid] || null;
  }

  UpdateViewCookies() {
    const prefs = {
      hide_empty_folders: this._hide_empty_folders,
      list_view: this._list_view,
      sort_alpha: this._sort_alpha
    };
    this.cookieService.set('fileExplorerPrefsClient', JSON.stringify(prefs), 30, '/');
  }

  RestoreViewCookies() {
    const raw = this.cookieService.get('fileExplorerPrefsClient');
    if (!raw) return;
    try {
      const prefs = JSON.parse(raw);
      if (prefs.hide_empty_folders !== undefined) this._hide_empty_folders = prefs.hide_empty_folders;
      if (prefs.list_view !== undefined) this._list_view = prefs.list_view;
      if (prefs.sort_alpha !== undefined) this._sort_alpha = prefs.sort_alpha;
    } catch (e) {}
  }


  //Gets the information about the current folder and gets all the first info about the root folder
  async Get(guid) {
    this.finished_loading = false;
    this.loading_more_pages = false;
    this.viewing_important_folder = false;
    this.load_generation++;
    const load_id = this.load_generation;
    let temp_data: any = await this.apiService.get(`files/${guid}?limit=${this.PAGE_SIZE}&offset=0`);
    const first_page_full = temp_data.success && Array.isArray(temp_data.data) && temp_data.data.length === this.PAGE_SIZE;
    if (temp_data.success) {
      this.data = temp_data.data;
      console.log("TEMP DATA", temp_data);
      if (temp_data.parent && temp_data.parent[0]) {
        this.current_folder = temp_data.parent[0];
      } else {
        console.error('Get(): backend returned no parent folder data for guid', guid, temp_data);
        this.current_folder = { guid: guid };
      }
    }
    else this.globalService.presentToastMessage(temp_data.message, temp_data.success);
    if (this.data == null) this.data = [];
    // Inject a synthetic "Important" folder tile at the root of the client's/company's own folder tree -
    // it's not a real file_system row, clicking it switches to the flat GetImportantFiles() view instead
    // of normal folder navigation.
    if (this.IsClientOrCompanyRootFolder()) {
      const entity_guid = this.current_folder.client_guid || this.current_folder.company_guid;
      this.data.unshift({
        guid: entity_guid,
        is_file: false,
        is_important_folder: true,
        folder_title: 'Important',
        file_count: null
      });
    }
    if (this.selected_files.length > 0) {
      //Loop through the files and if we get a match with a selected file, we need to make sure that file is marked as selected
      for (let i = 0; i < this.selected_files.length; i++) {
        this.selected_files[i].guid;
        var index = this.data.findIndex(item => item.guid === this.selected_files[i].guid);
        if (index > -1) this.data[index].selected = true;

      }
    }
    if (this.allow_hash_change)
      window.location.hash = this.current_folder.guid;
      


    //If the current item is not in the history, push it
    if(this.nav_history.findIndex(item => item.guid === this.current_folder.guid) == -1)
      this.nav_history.push(this.current_folder);
    
    // Debug: Log folders with files
    console.log("!!!!!!!!!!!!!!!");
    for (let folder of this.data) {
      if(folder.is_file == 0) {


      
      if (folder.file_count > 0) {
        console.log(folder);
      }
    }
    }
    this.finished_loading = true;
    this.GenerateBreadCrumbs();

    // The first page was full - there may be more, so keep fetching subsequent pages in the
    // background (not blocking finished_loading) until a page comes back short.
    if (first_page_full) {
      this.LoadRemainingPages(guid, load_id);
    }
  }

  // Background-loads subsequent pages (100 at a time) after the first page, for folders with
  // thousands of items - keeps the initial render fast without waiting on the whole folder's
  // contents. Aborts if the user has navigated elsewhere since this load started.
  async LoadRemainingPages(guid: string, load_id: number) {
    this.loading_more_pages = true;
    let offset = this.PAGE_SIZE;
    while (this.load_generation === load_id) {
      let page_data: any = await this.apiService.get(`files/${guid}?limit=${this.PAGE_SIZE}&offset=${offset}`);
      if (this.load_generation !== load_id) break;
      if (!page_data.success || !Array.isArray(page_data.data)) break;
      this.data.push(...page_data.data);
      if (page_data.data.length < this.PAGE_SIZE) break;
      offset += this.PAGE_SIZE;
    }
    // Only clear the flag if this is still the active load - if the user navigated elsewhere,
    // that newer Get() call owns loading_more_pages now.
    if (this.load_generation === load_id) this.loading_more_pages = false;
  }

  // Re-fetches the current folder's data in place, without touching nav_history/the URL hash - the
  // supported way to get fresh data instead of a full browser refresh (see the reload handling in
  // ngOnInit for why a plain F5 could otherwise break navigation into a client/company root folder).
  async RefreshCurrentFolder() {
    if (this.refreshing) return;
    this.refreshing = true;
    try {
      if (this.current_folder?.is_important_view) {
        const source = this.current_folder.source_folder;
        await this.GetImportantFiles(source.client_guid || source.company_guid, source);
      } else {
        await this.Get(this.current_folder.guid);
      }
    } finally {
      this.refreshing = false;
    }
  }

  async BreadcrumbClick(guid) {
    console.log("GUID: " + guid);
    var index = this.nav_history.findIndex(item => item.guid === guid);
    console.log(index);
    const target = this.nav_history[index];
    this.nav_history.splice(index+1, this.nav_history.length - (index+1) );
    if (target && target.is_important_view) {
      const source = target.source_folder;
      this.GetImportantFiles(source.client_guid || source.company_guid, source);
    } else {
      this.Get(guid);
    }
  }

  // True when the current folder IS a client/company's own root folder (their folder guid always
  // matches their own entity guid) - that's the only place the synthetic "Important" tile shows.
  IsClientOrCompanyRootFolder(): boolean {
    if (!this.current_folder) return false;
    const isRoot = this.current_folder.root_folder == 1 || this.current_folder.root_folder === '1' || this.current_folder.root_folder === true;
    return isRoot && !!(this.current_folder.client_guid || this.current_folder.company_guid);
  }

  // Flat view of every file marked important anywhere under this client/company's folder tree,
  // regardless of which real subfolder it actually lives in. source_folder is the client/company's
  // own root folder object (needed so breadcrumb navigation back into this view works).
  async GetImportantFiles(entity_guid: string, source_folder: any) {
    this.finished_loading = false;
    this.viewing_important_folder = true;
    const important_guid = 'important:' + entity_guid;
    let temp_data: any = await this.apiService.get(`files/${entity_guid}?important=1`);
    if (temp_data.success) {
      this.data = temp_data.data || [];
    } else {
      this.data = [];
      this.globalService.presentToastMessage(temp_data.message, temp_data.success);
    }
    this.current_folder = { guid: important_guid, folder_title: 'Important', is_important_view: true, source_folder };
    if (this.nav_history.findIndex(item => item.guid === this.current_folder.guid) == -1) {
      this.nav_history.push(this.current_folder);
    }
    this.finished_loading = true;
  }

  async ToggleImportant(file: any) {
    const new_value = !file.is_important;
    const data: any = await this.apiService.put(`files/${file.guid}/important`, { important: new_value });
    if (data.success) {
      file.is_important = new_value;
      this.globalService.presentToastMessage(new_value ? 'Marked as important' : 'Removed from important', true);
    } else {
      this.globalService.presentToastMessage(data.message, false);
    }
  }

  // The "Important" view is a flat aggregation of important files from across the whole folder
  // tree - there's no single real folder underlying it to create a folder in or upload a file
  // into, same restriction as the accountant side's Important view.
  UploadAndCreateRestricted(): boolean {
    return this.viewing_important_folder;
  }

  //When a file is dropped
  async dropped(files: NgxFileDropEntry[]) {
    if(this.nav_history.length == 1 || this.nav_history.length == 0) {
      this.globalService.presentToastMessage("You cannot upload files to the root directory, please select a folder first.", false);
      return;
    }
    if (this.UploadAndCreateRestricted()) {
      this.globalService.presentToastMessage("Files cannot be uploaded to the Important view - please select a real folder first.", false);
      return;
    }
    this.files = files;
    let any_uploaded = false;
    for (const droppedFile of files) {
      // Is it a file?
      if (droppedFile.fileEntry.isFile) {
        console.log("Present Loading");
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        await new Promise<void>(resolve => {
          fileEntry.file(async (file: File) => {
            let upload_data = await this.apiService.UploadFile(file, this.current_folder.guid);
            if (upload_data) any_uploaded = true;
            else this.globalService.presentToastMessage("Failed to upload file", false);
            resolve();
          });
        });
      } else {
        // It was a directory (empty directories are added, otherwise only files)
        const fileEntry = droppedFile.fileEntry as FileSystemDirectoryEntry;
      }
    }
    // Refresh once after every file has finished uploading, instead of once per file.
    if (any_uploaded) this.Get(this.current_folder.guid);
  }

  //Open the file selector for the dropzone
  openFileSelector() {
    let element: HTMLElement = document.getElementById("UploadFileButton");
    element.click();
  }

  //Presents the pop over for extra file options
  async presentFilePopover(e, file) {
    e.stopPropagation();
    e.preventDefault();
    const popover = await this.popoverController.create({
      component: FilePage,
      componentProps: {
        file: file,
        selected_files: this.selected_files
      },
      event: e,
      animated: true,
      backdropDismiss: true
    });
    popover.onDidDismiss()
      .then((data) => {
        console.log(data);
        // 0 = select file
        // 1 = share file
        // 2 = Move file
        // 3 = Copy file
        // 4 = Download file
        // 5 = comment
        // 6 = preview
        // 7 = delete
        // 8 = toggle important
        // null = cancel
        if (data.data == 0) this.SelectFile(e, file);
        else if (data.data == 1) {this.UnselectAll(); this.SelectFile(e, file); this.ShareSelected();}
        else if (data.data == 2) {this.UnselectAll(); this.SelectFile(e, file); this.MoveSelected();}
        else if (data.data == 3) {this.UnselectAll(); this.SelectFile(e, file); this.CopySelected();}
        else if (data.data == 4) this.apiService.DownloadFile(file);
        else if (data.data == 5) this.CommentOnFile(file);
        else if (data.data == 6) this.PreviewFile(file);
        else if (data.data == 7) this.DeleteFile(file);
        else if (data.data == 8) this.ToggleImportant(file);

      });
    return await popover.present();
  }

  //File options

  //Select and deselect a file, this is called on right click or pop-over option
  async SelectFile(e, file) {
    e.stopPropagation();
    e.preventDefault();
    file.selected = !file.selected;
    if (file.selected) {
      this.selected_files.push(file);
    }
    else {
      var index = this.selected_files.findIndex(item => item.guid === file.guid);
      if (index > -1)
        this.selected_files.splice(index, 1);
    }
    return false;
  }

  async ShareFile(file) {

  }

  //Move the file and then update the file / view. Returns null if the file is already in the directory we are trying to move it too
  async MoveFile(file, folder_guid) {
    if (file.parent_folder_guid != folder_guid) {
      var put_data = {
        folder_guid: folder_guid
      }
      this.globalService.presentLoading(`Moving ${file.file_name}`,30000);
      let data: any = await this.apiService.put(`files/move/${file.guid}`, put_data);
      //If its successful, we need to see if we need to remove it from the current directory
      if (data.success) {
        //Update the file to have the new path
        file = data.data[0];
        var index = this.data.findIndex(item => item.guid === file.guid);
        if (file.parent_folder_guid != this.current_folder.guid) {
          this.data.splice(index, 1);
        } else if(file.parent_folder_guid == this.current_folder.guid && index == -1){
          this.data.push(file);
        }
      }
      //Dismiss loading and show the message
      this.globalService.dismissLoading();
      this.globalService.presentToastMessage(data.message, data.success);

      return data;
    }
    return null;
  }

  //Copy the file, then add it to the current view if its supposed to be
  async CopyFile(file, folder_guid) {
    var put_data = {
      folder_guid: folder_guid
    }
    this.globalService.presentLoading(`Copying ${file.file_name}`,30000);
    let data: any = await this.apiService.post(`files/copy/${file.guid}`, put_data);
    //If its successful, we need to see if we need to remove it from the current directory
    if (data.success) {
        let new_file = data.data[0];
        if(new_file.parent_folder_guid == this.current_folder.guid){
          this.data.push(new_file);
        }
    }
    //Dismiss loading and show the message
    this.globalService.dismissLoading();
    this.globalService.presentToastMessage(data.message, data.success);
    return data;

  }

  async CommentOnFile(file) {
    const modal = await this.modalController.create({
      component: CommentPage,
      componentProps: {
        file: file,
      },
      backdropDismiss: true,
      cssClass: 'medium-modal'
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
  }

  async PreviewFile(file) {

  }

  //Delete a file
  async DeleteFile(file) {
    //Remove this ID from the current array
    let data: any = await this.apiService.delete(`files/${file.guid}`,null);
    if (data.success) {
      var index = this.data.findIndex(item => item.guid === file.guid);
      this.data.splice(index, 1);
    }
    this.globalService.presentToastMessage(data.message, data.success);
  }


  //Options for selected files
  async DownloadSelected() {
    let guid_array = [];
    for (const file of this.selected_files) {
      guid_array.push(file.guid);
    }
    this.apiService.DownloadAsZip(guid_array);
  }


  async ShareSelected() {

  }


  async MoveSelected() {
    const modal = await this.modalController.create({
      component: FolderSelectModalPage,
      componentProps: {
        selected_files: this.selected_files,
        inc_current_folder: this.current_folder,
        move: true
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      for (let file of this.selected_files) {
        await this.MoveFile(file, data.guid);
      }
      this.UnselectAll();

    }
  }

  async CopySelected() {
    const modal = await this.modalController.create({
      component: FolderSelectModalPage,
      componentProps: {
        selected_files: this.selected_files,
        inc_current_folder: this.current_folder,
        move: false
      },
      cssClass: 'large-modal',
      backdropDismiss: false,
    });


    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {
      //Loop through each selected file and move it
      for (let file of this.selected_files) {
        await this.CopyFile(file, data.guid);
      }
      this.UnselectAll();
    }
  }

  async UnselectAll() {
    for (const file of this.data) {
      file.selected = false;
    }
    this.selected_files = [];
  }

  SelectAllFiles() {
    for (const item of this.data) {
      if (item.is_file && !item.selected) {
        item.selected = true;
        this.selected_files.push(item);
      }
    }
  }

  SelectAllFolders() {
    for (const item of this.data) {
      if (!item.is_file && !item.is_important_folder && !item.selected && !this.ShouldHideFolder(item)) {
        item.selected = true;
        this.selected_files.push(item);
      }
    }
  }

  //Delete all selected files, then empty the selected array
  async DeleteSelected() {
    console.log("STart Delete");
    for (const file of this.selected_files) {
      this.globalService.presentLoading(`Deleting ${file.file_name}`,30000);
      await this.DeleteFile(file);
      this.globalService.dismissLoading();
    }
    console.log("Finish Delete");
    this.selected_files = [];
  }

  async PresentDeleteSelectedConfirm() {
    let file_list_html = "";
    for (const file of this.selected_files) {
      file_list_html += `<br> ${file.file_name}`
    }
    const alert = await this.alertController.create({
      header: 'Are you sure you would like to delete!',
      message: `${file_list_html}`,
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
          handler: () => {
            console.log("Cancel")
          },
        },
        {
          text: 'OK',
          role: 'confirm',
          handler: () => {
            this.DeleteSelected();
          },
        },
      ],
    });

    await alert.present();
  }


  //MODALs
  async OpenCreateFolderModal() {
    if(this.nav_history.length == 1 || this.nav_history.length == 0) {
      alert("You cannot create folders in the root directory, please select a folder first.");
      return;
    }
    if (this.UploadAndCreateRestricted()) {
      alert("Folders cannot be created in the Important view - please select a real folder first.");
      return;
    }
    const modal = await this.modalController.create({
      component: CreateFolderPage,
      componentProps: {
        current_folder: this.current_folder
      },
      backdropDismiss: false,
    });
    modal.present();

    const { data, role } = await modal.onWillDismiss();
    //Data is expected to return a create folder object that is
    // name, visible_to_client
    if (data) {

      this.data.push(data[0]);
    }
  }

//This method runs once if we loaded the location from the hash
//It loops through each path down to figure out the breadcrumbs
  async GenerateBreadCrumbs() {
    if(this.loaded_from_hash && !this.generated_from_hash) {
      let last_parent_guid = this.current_folder.guid;
      console.log("Last Parent: " + last_parent_guid);
      while (last_parent_guid) {
        let parent_data: any = await this.apiService.get(`folder/parent/${last_parent_guid}`);
        console.log("Parent Data", parent_data);
        if(parent_data.data) {
          this.nav_history.unshift(parent_data.data[0]);
          last_parent_guid = parent_data.data[0].guid;
        }
        else  {
          console.log("BREAK");
          break;
        } 
      }
      this.generated_from_hash = true;
    }
    this.loaded_breadcrumbs = true;
  }

  async CreateDocument() {

  }

  async Log() {
    console.log("!!!!!!!!!!!!!!!");
    for (let folder of this.data) {
      if(folder.is_file == 0) {


      
      if (folder.file_count > 0) {
        console.log(folder);
      }
    }
    }
  }

  getTableDescription(): string {
    const parts: string[] = [];

    if (this.hide_empty_folders) parts.push('empty folders hidden');

    if (this.sort_alpha === 'asc') parts.push('sorted A–Z');
    else if (this.sort_alpha === 'desc') parts.push('sorted Z–A');

    parts.push(this.list_view ? 'list view' : 'grid view');

    return parts.length === 0 ? '' : parts[0].charAt(0).toUpperCase() + parts[0].slice(1) + (parts.length > 1 ? ', ' + parts.slice(1).join(', ') : '') + '.';
  }

  ShouldHideFolder(folder) {
    if (this.hide_empty_folders && folder.file_count == 0) return true;
    return false;
  }

}
